<template>
  <v-list-item
    class="d-flex align-center"
    link
    @keydown="$emit('keydown-emit', $event)"
    @keydown.enter.prevent="emitActivate"
    @keydown.space.prevent="emitActivate"
    @focus="$emit('focus', $event)"
    @click="emitActivate"
  >
    <v-list-item-avatar
      tile
      class="rounded-lg"
      :class="{ background2: !rawStatusValue }"
      :style="avatarStyle"
      size="50"
    >
      <v-icon
        v-if="rawStatusValue"
        size="26"
        :color="statusColor"
        v-text="statusIconSymbol"
      ></v-icon>
      <v-icon v-else size="26" color="grey darken-1">
        mdi-receipt-text-outline
      </v-icon>
    </v-list-item-avatar>

    <v-list-item-content class="py-2">
      <v-list-item-title>
        <span class="text-wrap font-weight-bold">
          {{ internalInvoiceLabel
          }}<span
            v-if="externalInvoiceParen"
            class="font-weight-medium text--secondary"
          >
            {{ externalInvoiceParen }}</span
          >
        </span>
      </v-list-item-title>
      <v-list-item-subtitle>
        <span
          class="text-capitalize text-wrap text-body-2 font-weight-medium text--secondary"
        >
          {{ accountSubtitleLine }}
        </span>
      </v-list-item-subtitle>
      <v-list-item-subtitle class="mt-1">
        <span class="text-caption text-wrap">
          <span class="font-weight-bold">Total:</span>
          <span
            class="font-weight-bold ml-1"
            :style="{ color: $vuetify.theme.currentTheme.primary }"
            >{{ formatOrderCurrency(totalAmount) }}</span
          >
          <span class="mx-1 text--disabled">·</span>
          <span class="font-weight-bold">Paid:</span>
          <span
            class="font-weight-bold ml-1"
            :style="{ color: $vuetify.theme.currentTheme.success }"
            >{{ formatOrderCurrency(paidAmount) }}</span
          >
          <span class="mx-1 text--disabled">·</span>
          <span class="font-weight-bold">Balance:</span>
          <span
            class="font-weight-bold ml-1"
            :style="{ color: $vuetify.theme.currentTheme.error }"
            >{{ formatOrderCurrency(balanceAmount) }}</span
          >
        </span>
      </v-list-item-subtitle>
    </v-list-item-content>

    <v-list-item-action>
      <div class="d-flex" style="gap: 8px">
        <StatusChip
          v-if="showStatus && statusChipPayload"
          no-top-margin
          :status="statusChipPayload"
          class="text-capitalize"
        />
        <v-icon small color="icon">mdi-open-in-new</v-icon>
      </div>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import { mapGetters } from "vuex";
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";

export default {
  name: "OrderCell",
  components: {
    StatusChip,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters(["getOrderStatus"]),
    nestedBalance() {
      const o = this.order || {};
      return o.balance && typeof o.balance === "object" ? o.balance : null;
    },
    internalInvoiceLabel() {
      const o = this.order || {};
      const n =
        o["orderNumber.internal"] ??
        o.orderNumberInternal ??
        o.order_internal ??
        o?.orderNumber?.internal;
      const s = n != null && String(n).trim() !== "" ? String(n).trim() : "";
      const label = s || this.fallbackId || "Invoice";
      return String(label).toUpperCase();
    },
    externalInvoiceRaw() {
      const o = this.order || {};
      const n =
        o["orderNumber.external"] ??
        o.orderNumberExternal ??
        o.order_external ??
        o?.orderNumber?.external;
      return n != null && String(n).trim() !== "" ? String(n).trim() : "";
    },
    externalInvoiceParen() {
      const ext = this.externalInvoiceRaw;
      return ext ? ` (${ext})` : "";
    },
    accountNameLabel() {
      const o = this.order || {};
      const n =
        o["account.name"] ??
        o.accountName ??
        o.account_name ??
        o?.account?.name ??
        "";
      return String(n).trim();
    },
    brandLabel() {
      const o = this.order || {};
      const n =
        o["brand.name"] ??
        o.brandName ??
        o.brand_name ??
        o?.brand?.name ??
        (typeof o.brand === "string" ? o.brand : "") ??
        "";
      return String(n).trim();
    },
    accountSubtitleLine() {
      const account = this.accountNameLabel;
      const brand = this.brandLabel;
      if (account && brand) return `${account} · ${brand}`;
      if (account) return account;
      if (brand) return brand;
      return "—";
    },
    fallbackId() {
      const o = this.order || {};
      return String(
        o.id || o.objectID || o.salesOrderId || o.orderId || "",
      ).trim();
    },
    totalAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const value =
        o.balanceTotal ??
        o.balance_total ??
        nested?.total ??
        o.total ??
        o.balance?.total;
      return Number(value) || 0;
    },
    dueAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const value =
        o.balanceDue ?? o.balance_due ?? nested?.due ?? o.due ?? o.balance?.due;
      return Number(value) || 0;
    },
    paidAmount() {
      const paid = this.totalAmount - this.dueAmount;
      return paid >= 0 ? paid : 0;
    },
    balanceAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const explicit =
        o.balanceRemaining ??
        o.balance_remaining ??
        o.outstandingBalance ??
        o.outstanding_balance ??
        nested?.balance ??
        nested?.remaining;
      if (explicit != null && String(explicit).trim() !== "") {
        const n = Number(explicit);
        if (!Number.isNaN(n)) return n;
      }
      return this.dueAmount;
    },
    rawStatusValue() {
      return String(this.order?.status ?? "").trim();
    },
    resolvedStatus() {
      const list = this.getOrderStatus || [];
      const raw = this.rawStatusValue;
      if (!raw) return null;
      let found = list.find((o) => o.value === raw);
      if (!found) {
        const lower = raw.toLowerCase();
        found = list.find((o) => o.value.toLowerCase() === lower);
      }
      return found || null;
    },
    statusDisplayText() {
      const raw = this.rawStatusValue;
      if (!raw) return "";
      if (this.resolvedStatus) return this.resolvedStatus.text;
      return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    },
    statusColor() {
      return this.resolvedStatus?.color ?? "#9e9e9e";
    },
    /** Same resolution as SalesOrder_New.vue (previous orders avatar). */
    statusColorResolved() {
      const c = this.statusColor;
      if (["error", "products"].includes(c)) {
        return this.$vuetify.theme.currentTheme[c];
      }
      return c;
    },
    avatarStyle() {
      if (!this.rawStatusValue) {
        return {
          boxShadow:
            "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
          border: `1px solid ${this.$vuetify.theme.currentTheme.background3} !important`,
        };
      }
      return {
        background: `${this.statusColorResolved}33`,
      };
    },
    statusChipPayload() {
      if (!this.rawStatusValue) return null;
      return {
        value: this.resolvedStatus?.value ?? this.rawStatusValue,
        text: this.statusDisplayText,
        color: this.statusColor,
      };
    },
    statusIconSymbol() {
      const value = this.resolvedStatus?.value ?? this.rawStatusValue;
      return this.getStatusIconForValue(value);
    },
    dateText() {
      return this.getOrderHitDateText(this.order);
    },
  },
  methods: {
    /**
     * Matches SalesOrder_New.vue (previous orders list): icon set for each status.value.
     */
    getStatusIconForValue(value) {
      const v = String(value || "").trim();
      if (!v) return "$delete";
      switch (v) {
        case "complete":
          return "$checkCircle";
        case "shipped":
          return "$delivery";
        case "fulfilled":
          return "$deliveryReady";
        case "deliveryConfirmation":
          return "$deliveryConfirmation";
        case "accepted":
          return "$orderComplete";
        case "submitted":
          return "$order";
        case "draft":
          return "$awaitingApproval";
        default:
          break;
      }
      const lower = v.toLowerCase();
      const byLower = {
        complete: "$checkCircle",
        shipped: "$delivery",
        fulfilled: "$deliveryReady",
        deliveryconfirmation: "$deliveryConfirmation",
        accepted: "$orderComplete",
        submitted: "$order",
        draft: "$awaitingApproval",
        cancelled: "$delete",
        rejected: "$delete",
      };
      return byLower[lower] ?? "$delete";
    },
    emitActivate() {
      this.$emit("activate", this.order);
    },
    formatOrderCurrency(value) {
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value) || 0);
      } catch (error) {
        console.error("Error formatting order currency:", error);
        return "$0.00";
      }
    },
    getOrderHitDateText(orderHit) {
      const rawDate =
        orderHit?.shipDate ||
        orderHit?.ship_date ||
        orderHit?.created ||
        orderHit?.created_at ||
        orderHit?.lastmodified ||
        orderHit?.lastModified;
      if (rawDate == null) return "";
      const parsed =
        typeof rawDate === "number" ? new Date(rawDate) : new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return "";
      return `Date: ${parsed.toLocaleDateString()}`;
    },
  },
};
</script>
